import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';

const SurveyTaker = () => {
  // HOOKS AL INICIO
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [tokenValid, setTokenValid] = useState(null); // null: no validado, true: válido, false: inválido
  const [survey, setSurvey] = useState(null);
  // Eliminado estado de pregunta actual (no se usa)
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = React.useRef();
  const [currentPage, setCurrentPage] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef();
  const autoSubmittedRef = useRef(false);
  const [tokenStatus, setTokenStatus] = useState(null); // null, 'in_progress', 'completed', 'ok'
  const [statusMsg, setStatusMsg] = useState('');
  // Guardar startedAt global del backend
  const [startedAt, setStartedAt] = useState(null);
  // Datos personales (no usados en UI); evitar warning removiendo estado si no se utilizará
  // const [respondentPersonalData, setRespondentPersonalData] = useState({});

  // Helper: determinar si la encuesta está realmente disponible para responder
  const isSurveyOpen = (s) => {
    if (!s) return false;
    const now = new Date();
    const start = s.startDate ? new Date(s.startDate) : null;
    const end = s.endDate ? new Date(s.endDate) : null;
    const fueraDeRango = (start && now < start) || (end && now > end);
    const maxReached = s.maxResponses && s.responseCount >= Number(s.maxResponses);
    return !!s.isActive && !fueraDeRango && !maxReached;
  };

  // Validar token y estado global al cargar
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    api.get(`/api/survey-token/validate/${id}/${token}`)
      .then(res => {
        setTokenValid(res.data.valid);
        if (res.data.valid) {
          // Consultar estado global del token
          api.get(`/api/survey-token/status?surveyId=${id}`)
            .then(resp => {
              const found = resp.data.find(t => t.token === token);
              if (found) {
                // Guardar datos personales
                // Datos personales disponibles si se requieren en el futuro
                if (found.estado === 'Completado') {
                  setTokenStatus('completed');
                  setStatusMsg('Ya has respondido esta encuesta. Solo se permite una respuesta por usuario.');
                } else if (found.estado === 'En Curso') {
                  setTokenStatus('in_progress');
                  setStatusMsg('La encuesta ya está abierta o en desarrollo en otro dispositivo o pestaña.');
                  setStartedAt(found.startedAt ? new Date(found.startedAt) : null);
                } else {
                  setTokenStatus('ok');
                  setStartedAt(null);
                }
              } else {
                setTokenStatus('invalid');
                setStatusMsg('Token inválido o no encontrado.');
                setStartedAt(null);
              }
            })
            .catch(() => {
              setTokenStatus('error');
              setStatusMsg('No se pudo verificar el estado del acceso.');
              setStartedAt(null);
            });
        }
      })
      .catch(() => setTokenValid(false));
  }, [id, token]);

  // Fetch de la encuesta
  const fetchSurvey = useCallback(async () => {
    try {
      const response = await api.get(`/api/forms/${id}/open`);
      const data = response.data;
      // Compatibilidad: si no hay pages, pero sí questions, inicializo pages
      if (!data.pages && Array.isArray(data.questions)) {
        data.pages = [data.questions];
      }
      // Compatibilidad: si no hay questions pero sí pages, genero questions plano
      if (!data.questions && Array.isArray(data.pages)) {
        data.questions = data.pages.flat();
      }
      setSurvey(data);

      // Initialize answers object
      const initialAnswers = {};
      data.questions.forEach((question, index) => {
        if (question.type === 'checkbox') {
          initialAnswers[index] = [];
        } else if (question.type === 'scale') {
          initialAnswers[index] = null;
        } else {
          initialAnswers[index] = '';
        }
      });
      setAnswers(initialAnswers);
    } catch (error) {
      toast.error('Encuesta no encontrada o no disponible');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (tokenValid !== true) return;
    fetchSurvey();
  }, [tokenValid, fetchSurvey]);

  // Scroll al inicio al cambiar de página
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Unificar handleAutoSubmit para aceptar mensaje opcional (debe definirse antes de efectos que lo usan)
  const [autoMsg, setAutoMsg] = useState(null);
  const handleAutoSubmit = useCallback(async (msg) => {
    if (autoSubmittedRef.current) return;
    autoSubmittedRef.current = true;
    setSubmitting(true);
    try {
      // Construir el mismo payload que handleSubmit: [{ questionId, answer }]
      const allAnswers = [];
      (survey.pages || [survey.questions]).forEach((page, pageIdx) => {
        const questionsArr = Array.isArray(page) ? page : (page && Array.isArray(page.questions) ? page.questions : []);
        questionsArr.forEach((q, qIdx) => {
          let ans = answers[pageIdx]?.[qIdx];
          if (q.type === 'slider' && Array.isArray(q.sliders)) {
            ans = Array.isArray(ans) ? ans : [];
            ans = q.sliders.map((slider, sIdx) => (ans[sIdx] !== undefined ? ans[sIdx] : slider.min));
          }
          if (q.type === 'file_upload' && ans && ans.filePath) {
            // mantener objeto con filePath
          }
          allAnswers.push({ questionId: q.id, answer: ans ?? '' });
        });
      });
      await api.post(`/api/forms/${id}/responses`, {
        answers: allAnswers,
        respondentInfo: {
          submittedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          autoSubmitted: true
        },
        token
      });
      setSubmitted(true);
      setAutoMsg(msg || '¡Tiempo agotado! Tus respuestas fueron enviadas automáticamente.');
    } catch (error) {
      toast.error('No se pudo enviar la encuesta automáticamente');
    } finally {
      setSubmitting(false);
    }
  }, [answers, id, survey, token]);

  // Iniciar temporizador cuando survey esté cargado y tenga timeLimit y startedAt, y la encuesta esté abierta
  useEffect(() => {
    if (survey && isSurveyOpen(survey) && survey.timeLimit && (startedAt || startedAt === null)) {
      const [h, m, s] = survey.timeLimit.split(':').map(Number);
      const totalSeconds = (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
      if (startedAt) {
        const now = new Date();
        const elapsed = Math.floor((now - startedAt) / 1000);
        setTimeLeft(Math.max(totalSeconds - elapsed, 0));
      } else {
        setTimeLeft(totalSeconds);
      }
    }
  }, [survey, startedAt]);

  // Temporizador regresivo
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || submitted) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timeLeft, submitted, handleAutoSubmit]);

  // Autoenvío solo si la encuesta se deshabilita por fecha/hora mientras está ABIERTA en esta sesión
  useEffect(() => {
    if (!survey || submitted) return;
    // Si la encuesta no está disponible al ingresar, no autoenviar
    if (!isSurveyOpen(survey)) return;
    const now = new Date();
    const end = survey.endDate ? new Date(survey.endDate) : null;
    if (!end) return;
    const msToEnd = end - now;
    // Si ya terminó al cargar, no autoenviar (mostrar pantalla inactiva)
    if (msToEnd <= 0) return;
    const timeout = setTimeout(() => {
      handleAutoSubmit('El tiempo para responder la encuesta ha finalizado. Tus respuestas han sido enviadas automáticamente.');
    }, msToEnd);
    return () => clearTimeout(timeout);
  }, [survey, submitted, handleAutoSubmit]);

  // Marcar 'En Curso' solo si el token está ok y la encuesta está abierta
  useEffect(() => {
    if (!id || !token || tokenStatus !== 'ok' || !isSurveyOpen(survey)) return;
    api.post('/api/form-token/mark-in-progress', { formId: id, token }).catch(() => { });
  }, [id, token, tokenStatus, survey]);

  // Advertencia antes de cerrar la pestaña/ventana/app solo si la encuesta está abierta
  useEffect(() => {
    if (!id || !token || tokenStatus !== 'ok' || submitted || !isSurveyOpen(survey)) return;
    const handleBeforeUnload = (e) => {
      const confirmationMessage = '¿Estás seguro de que quieres salir? Si cierras la ventana, la encuesta se enviará automáticamente con tus respuestas marcadas y ya no podrás volver a ingresar ni resolver la encuesta.';
      e.preventDefault();
      e.returnValue = confirmationMessage;
      return confirmationMessage;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [id, token, tokenStatus, submitted, survey]);

  // Limpieza al cerrar realmente solo si la encuesta está abierta (sin autoenvío)
  useEffect(() => {
    if (!id || !token || tokenStatus !== 'ok' || submitted || !isSurveyOpen(survey)) return;
    const handleUnload = () => {
      const data = JSON.stringify({ formId: id, token });
      const blob = new Blob([data], { type: 'application/json' });
      navigator.sendBeacon && navigator.sendBeacon('/api/form-token/clear-in-progress', blob);
    };
    window.addEventListener('unload', handleUnload);
    return () => {
      window.removeEventListener('unload', handleUnload);
    };
  }, [id, token, tokenStatus, submitted, survey]);

  // Limpiar estado "En Curso" al cerrar/desmontar; se intenta siempre, pero solo habrá estado si estuvo abierta
  useEffect(() => {
    const handleUnload = async () => {
      if (!submitted && token && id) {
        try {
          await api.post('/api/form-token/clear-in-progress', { formId: id, token });
          await api.get(`/api/form-token/status?formId=${id}`);
          // Notificar a la vista de compartidos que debe refrescar
          window.dispatchEvent(new Event('refreshCompartidos'));
        } catch (e) { }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
  }, [submitted, token, id]);

  // Heartbeat/ping solo si la encuesta está abierta
  useEffect(() => {
    if (!id || !token || (tokenStatus !== 'ok' && tokenStatus !== 'in_progress') || submitted || !isSurveyOpen(survey)) return;
    const interval = setInterval(() => {
      api.post('/api/survey-token/mark-in-progress', { surveyId: id, token }).catch(() => { });
    }, 5000); // cada 5 segundos
    return () => clearInterval(interval);
  }, [id, token, tokenStatus, submitted, survey]);

  // Efecto para textareas automáticas
  useEffect(() => {
    document.querySelectorAll('.response-input-taker').forEach(textarea => {
      textarea.addEventListener('input', () => {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      });
      textarea.dispatchEvent(new Event('input'));
    });
  }, [currentPage, survey]);

  // Returns condicionales DESPUÉS de los hooks
  if (!token || tokenValid === false || tokenStatus === 'invalid') {
    return (
      <div className="survey-taker-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa' }}>
        <div style={{
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
          padding: '48px 36px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          margin: '40px 0',
          color: '#dc3545',
          fontWeight: 600,
          fontSize: '1.3rem',
          border: '2px solid #dc3545',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18
        }}>
          <div style={{ fontSize: 54, marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#dc3545', marginBottom: 10 }}>Acceso denegado</div>
          <div style={{ color: '#555', fontWeight: 400, fontSize: '1.1rem' }}>
            No tienes permisos para acceder a esta encuesta.<br />
            Verifica que el enlace sea correcto o contacta al administrador.
          </div>
        </div>
      </div>
    );
  }
  if (tokenStatus === 'completed') {
    return (
      <div className="survey-taker-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa' }}>
        <div style={{
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
          padding: '48px 36px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          margin: '40px 0',
          color: '#19B394',
          fontWeight: 600,
          fontSize: '1.3rem',
          border: '2px solid #19B394',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18
        }}>
          <div style={{ fontSize: 54, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#19B394', marginBottom: 10 }}>Encuesta ya respondida</div>
          <div style={{ color: '#555', fontWeight: 400, fontSize: '1.1rem' }}>{statusMsg}</div>
        </div>
      </div>
    );
  }
  if (tokenStatus === 'in_progress') {
    return (
      <div className="survey-taker-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa' }}>
        <div style={{
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
          padding: '48px 36px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          margin: '40px 0',
          color: '#007bff',
          fontWeight: 600,
          fontSize: '1.3rem',
          border: '2px solid #007bff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18
        }}>
          <div style={{ fontSize: 54, marginBottom: 8 }}>🕒</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#007bff', marginBottom: 10 }}>Encuesta en curso</div>
          <div style={{ color: '#555', fontWeight: 400, fontSize: '1.1rem' }}>{statusMsg}</div>
        </div>
      </div>
    );
  }
  if (tokenValid === null) {
    return null;
  }



  // Cambiar handleAnswerChange y handleCheckboxChange para guardar el texto de la opción
  const handleAnswerChange = (pageIndex, value) => {
    setAnswers(prev => ({
      ...prev,
      [pageIndex]: value
    }));
  };

  const handleCheckboxChange = (pageIndex, questionIndex, option, checked) => {
    setAnswers(prev => {
      const pageAnswers = prev[pageIndex] ? { ...prev[pageIndex] } : {};
      const currentAnswers = Array.isArray(pageAnswers[questionIndex]) ? pageAnswers[questionIndex] : [];
      let updatedAnswers;
      if (checked) {
        // Agregar el texto de la opción
        updatedAnswers = [...currentAnswers, option];
      } else {
        updatedAnswers = currentAnswers.filter(ans => ans !== option);
      }
      return {
        ...prev,
        [pageIndex]: {
          ...pageAnswers,
          [questionIndex]: updatedAnswers
        }
      };
    });
  };

  // Eliminados helpers no usados (nextQuestion, prevQuestion, isCurrentQuestionValid)

  const handleSubmit = async () => {
    // Validar todas las preguntas obligatorias de todas las páginas
    let missing = false;
    (survey.pages || [survey.questions]).forEach((page, pageIdx) => {
      const questionsArr = Array.isArray(page) ? page : (page && Array.isArray(page.questions) ? page.questions : []);
      questionsArr.forEach((question, qIdx) => {
        if (question.required && !isQuestionValid(question, answers[pageIdx]?.[qIdx])) {
          missing = true;
        }
      });
    });
    if (missing) {
      toast.error('Por favor responde todas las preguntas obligatorias');
      return;
    }
    setSubmitting(true);
    try {
      // Guardar respuestas como array de objetos { questionId, answer }
      const allQuestions = [];
      const allAnswers = [];
      (survey.pages || [survey.questions]).forEach((page, pageIdx) => {
        const questionsArr = Array.isArray(page) ? page : (page && Array.isArray(page.questions) ? page.questions : []);
        questionsArr.forEach((q, qIdx) => {
          allQuestions.push(q);
          let ans = answers[pageIdx]?.[qIdx];
          if (q.type === 'slider' && Array.isArray(q.sliders)) {
            ans = Array.isArray(ans) ? ans : [];
            ans = q.sliders.map((slider, sIdx) =>
              ans[sIdx] !== undefined ? ans[sIdx] : slider.min
            );
          }
          // Si es upload, mantener objeto; no hace falta re-asignación redundante
          // if (q.type === 'file_upload' && ans && ans.filePath) { }
          allAnswers.push({ questionId: q.id, answer: ans ?? '' });
        });
      });
      // Marcar enviado ANTES de esperar para evitar que el unload limpie o modifique estado
      setSubmitted(true);
      await api.post(`/api/forms/${id}/responses`, {
        answers: allAnswers,
        token
      });
      toast.success('¡Encuesta enviada exitosamente!');
    } catch (error) {
      toast.error('No se pudo enviar la encuesta');
      // Si falló, revertir submitted para permitir reintento
      setSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Eliminado formatTime no usado

  // Formatear tiempo HH : MM : SS
  const formatTimeFull = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return { h, m, s };
  };

  // Nueva función para subir archivo al backend y obtener la ruta
  async function uploadFileTobackend(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/api/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.filePath;
  }

  if (loading) {
    return <div className="loading">Loading survey...</div>;
  }

  if (!survey) {
    return <div className="error">Survey not found</div>;
  }

  // Bloquear acceso si la encuesta tiene fechas y la fecha actual está fuera del rango
  const now = new Date();
  const start = survey.startDate ? new Date(survey.startDate) : null;
  const end = survey.endDate ? new Date(survey.endDate) : null;
  const fueraDeRango = (start && now < start) || (end && now > end);
  // Bloquear acceso si se alcanzó el máximo de respuestas
  const maxReached = survey.maxResponses && survey.responseCount >= Number(survey.maxResponses);

  if (!survey.isActive || fueraDeRango || maxReached) {
    return (
      <div className="survey-taker-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa' }}>
        <div style={{
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
          padding: '48px 36px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          margin: '40px 0',
          color: '#dc3545',
          fontWeight: 600,
          fontSize: '1.3rem',
          border: '2px solid #dc3545',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18
        }}>
          <div style={{ fontSize: 54, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#dc3545', marginBottom: 10 }}>Encuesta no disponible</div>
          <div style={{ color: '#555', fontWeight: 400, fontSize: '1.1rem' }}>
            Esta encuesta está inactiva, fuera de fecha o ya alcanzó el máximo de respuestas.<br />
            Si crees que esto es un error, contacta al administrador.
          </div>
        </div>
      </div>
    );
  }

  // Vista cuando se autoenvía por tiempo/fin de disponibilidad
  if (submitted && autoMsg) {
    return (
      <div className="survey-taker-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eefdfa' }}>
        <div style={{
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
          padding: '48px 36px',
          maxWidth: 560,
          width: '100%',
          textAlign: 'center',
          margin: '40px 0',
          color: '#19B394',
          fontWeight: 600,
          fontSize: '1.2rem',
          border: '2px solid #19B394',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14
        }}>
          <div style={{ fontSize: 54, marginBottom: 8 }}>⏰</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#19B394', marginBottom: 6 }}>Tiempo finalizado</div>
          <div style={{ color: '#555', fontWeight: 400, fontSize: '1.06rem' }}>
            {autoMsg || 'El tiempo se agotó y tus respuestas fueron enviadas automáticamente.'}
          </div>
        </div>
      </div>
    );
  }

  if (submitted && !autoMsg) {
    return (
      <div className="survey-taker-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eefdfa' }}>
        <div style={{
          background: '#fff',
          borderRadius: '18px',
          boxShadow: '0 4px 24px rgba(25,179,148,0.10)',
          padding: '48px 36px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          margin: '40px 0',
          color: '#19B394',
          fontWeight: 600,
          fontSize: '1.3rem',
          border: '2px solid #19B394',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18
        }}>

          <div style={{ fontSize: 54, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#19B394', marginBottom: 10 }}>¡Encuesta enviada!</div>
          <div style={{ color: '#555', fontWeight: 400, fontSize: '1.1rem' }}>
            ¡Gracias por tu respuesta!<br />Tu participación ha sido registrada exitosamente.
          </div>
        </div>
      </div>
    );
  }

  const pages = survey.pages || [survey.questions];
  const page = pages[currentPage];
  const questions = Array.isArray(page) ? page : (page && Array.isArray(page.questions) ? page.questions : []);
  // Eliminado progress no usado

  return (
    <div className="survey-taker-container" style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Temporizador responsivo */}
      {survey.timeLimit && timeLeft !== null && timeLeft > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 18,
            right: 18,
            zIndex: 999,
            background: '#EDF9F7',
            borderRadius: 10,
            boxShadow: '0 2px 8px #19b39422',
            padding: '10px 18px 6px 18px',
            minWidth: 140,
            textAlign: 'center',
            border: '2px solid #19B394',
            width: 'auto',
            maxWidth: '95vw',
            ...(window.innerWidth <= 600
              ? {
                position: 'static',
                margin: '12px auto 8px auto',
                width: '90vw',
                minWidth: 0,
                padding: '7px 0 4px 0',
                fontSize: 11,
                borderRadius: 8,
              }
              : {}),
          }}
        >
          <div style={{ fontWeight: 700, fontSize: window.innerWidth <= 600 ? 11 : 13, color: '#19B394', letterSpacing: 1, marginBottom: 3 }}>TEMPORIZADOR</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: window.innerWidth <= 600 ? 15 : 22, fontWeight: 700, color: '#222', letterSpacing: 2, marginBottom: 1 }}>
            {formatTimeFull(timeLeft).h} <span style={{ margin: window.innerWidth <= 600 ? '0 3px' : '0 5px', fontWeight: 400, color: '#19B394' }}> : </span> {formatTimeFull(timeLeft).m} <span style={{ margin: window.innerWidth <= 600 ? '0 3px' : '0 5px', fontWeight: 400, color: '#19B394' }}> : </span> {formatTimeFull(timeLeft).s}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: window.innerWidth <= 600 ? 7 : 16, marginTop: 0 }}>
            <span style={{ fontSize: window.innerWidth <= 600 ? 7.5 : 9.5, color: '#19B394', fontWeight: 600 }}>HORAS</span>
            <span style={{ fontSize: window.innerWidth <= 600 ? 7.5 : 9.5, color: '#19B394', fontWeight: 600 }}>MINUTOS</span>
            <span style={{ fontSize: window.innerWidth <= 600 ? 7.5 : 9.5, color: '#19B394', fontWeight: 600 }}>SEGUNDOS</span>
          </div>
        </div>
      )}
      <div className="survey-taker" style={window.innerWidth <= 600 ? { padding: '0 4vw' } : {}}>
        <div className="survey-header">
          <h1 className="survey-taker-title">{survey.title}</h1>
          {survey.description && (
            <div className="survey-taker-description" style={{ whiteSpace: 'pre-line', textAlign: 'justify' }}>
              {survey.description}
            </div>
          )}
        </div>



        {/* Indicador de página actual */}
        {page.pageTitle && (
          <h2 className="survey-taker-page-title" style={{
            fontSize: '1.2rem',
            fontWeight: 600,
            color: '#222',
            textAlign: 'left',
            margin: '20px 0 5px 10px',
            letterSpacing: '0.01em',
            fontFamily: 'Inter, Segoe UI, Arial, sans-serif'
          }}>{page.pageTitle}</h2>
        )}
        {page.pageDescription && (
          <p className="survey-taker-page-description" style={{
            fontSize: '1.08rem',
            color: '#5a5a5a',
            textAlign: 'left',
            margin: '0 0 5px 10px',
            marginTop: 0,
            fontWeight: 400,
            lineHeight: 1.5,
            fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
            whiteSpace: 'pre-line'
          }}>{page.pageDescription}</p>
        )}

        <div className="card">

          <div className="question-item">
            {questions.map((question, idx) => (
              <div key={idx} className="question-taker">
                <p className="question-title" >
                  {question.title}
                  {question.required && <span style={{ color: '#dc3545' }}> *</span>}
                </p>

                {question.type === 'text' && (
                  <div className="form-group">
                    <textarea
                      value={answers[currentPage]?.[idx] || ''}
                      onChange={e => handleAnswerChange(currentPage, {
                        ...answers[currentPage],
                        [idx]: e.target.value
                      })}
                      className="response-input-taker"
                      rows="1"
                      required={question.required}
                    />
                  </div>
                )}

                {question.type === 'multiple_choice' && (
                  <div className="choice-builder-container" style={{ marginBottom: '0px' }}>
                    {question.options.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className="option-builder"
                        onClick={() => handleAnswerChange(currentPage, {
                          ...answers[currentPage],
                          [idx]: option
                        })}
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          className="option-radio"
                          type="radio"
                          name={`question-${currentPage}-${idx}`}
                          value={option}
                          checked={answers[currentPage]?.[idx] === option}
                          onChange={() => handleAnswerChange(currentPage, {
                            ...answers[currentPage],
                            [idx]: option
                          })}
                          required={question.required}
                        />
                        <span className="option-edit-input">{option}</span>
                      </div>
                    ))}
                  </div>
                )}

                {question.type === 'checkbox' && (
                  <div className="question-options">
                    {question.options.map((option, optionIndex) => {
                      const answerArr = Array.isArray(answers[currentPage]?.[idx]) ? answers[currentPage][idx] : [];
                      return (
                        <div
                          key={optionIndex}
                          className={`option-item ${answerArr.includes(option) ? 'selected' : ''}`}
                          onClick={() => handleCheckboxChange(currentPage, idx, option, !answerArr.includes(option))}
                        >
                          <input
                            type="checkbox"
                            value={option}
                            checked={answerArr.includes(option)}
                            onChange={e => handleCheckboxChange(currentPage, idx, option, e.target.checked)}
                          />
                          <span>{option}</span>
                          {answerArr.includes(option) && (
                            <Check size={16} style={{ marginLeft: 'auto', color: '#129859' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {question.type === 'scale' && (
                  <div className="scale-taker-row">

                    <div className="scale-circles-row">
                      {Array.from({ length: (question.max - question.min + 1) || 11 }, (_, i) => question.min + i).map((val, index) => {
                        const total = (question.max - question.min + 1) || 11;
                        const scaleType = question.scaleType || 'numbers';
                        const isSelected = answers[currentPage]?.[idx] === val;
                        const selectedValue = answers[currentPage]?.[idx];
                        // Mapear índice de carita a escala 1-10 para colores consistentes
                        const mappedFace = Math.max(1, Math.min(10, Math.round(1 + (index / (total - 1)) * 9)));

                        // Función para obtener el ícono de Material Symbols
                        const getMaterialIcon = (iconName) => (
                          <span className="material-symbols-outlined" style={{ fontSize: '48px' }}>
                            {iconName}
                          </span>
                        );

                        // Función para obtener el ícono de carita según la posición
                        const getFaceIcon = (index, total) => {
                          if (total === 10) {
                            const faceIcons = [
                              'sentiment_very_dissatisfied', 'sentiment_extremely_dissatisfied', 'sentiment_stressed',
                              'sentiment_sad', 'sentiment_dissatisfied', 'sentiment_neutral', 'sentiment_satisfied',
                              'mood', 'sentiment_very_satisfied', 'sentiment_excited'
                            ];
                            return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                          }
                          if (total === 5) {
                            const faceIcons = ['sentiment_very_dissatisfied', 'sentiment_dissatisfied', 'sentiment_neutral', 'sentiment_satisfied', 'sentiment_very_satisfied'];
                            return getMaterialIcon(faceIcons[index] || faceIcons[0]);
                          }
                          // Fallback para otros totales
                          const progress = index / (total - 1);
                          if (progress <= 0.33) return getMaterialIcon('sentiment_dissatisfied');
                          else if (progress <= 0.66) return getMaterialIcon('sentiment_neutral');
                          else return getMaterialIcon('sentiment_satisfied');
                        };

                        return (
                          <label
                            key={val}
                            className={`scale-circle-label ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleAnswerChange(currentPage, {
                              ...answers[currentPage],
                              [idx]: val
                            })}
                            style={{ cursor: 'pointer' }}
                          >
                            {scaleType === 'numbers' && <span className="scale-number">{val}</span>}
                            {scaleType === 'stars' && (
                              <div
                                className={`scale-star-container ${selectedValue && val <= selectedValue ? 'star-selected' : ''
                                  }`}
                                onMouseEnter={() => {
                                  // Hover progresivo: iluminar estrellas hasta esta posición
                                  const allStars = document.querySelectorAll(`[name="scale-${currentPage}-${idx}"]`).forEach((input, starIndex) => {
                                    const starContainer = input.closest('.scale-circle-label').querySelector('.scale-star-container');
                                    if (starIndex < index) {
                                      starContainer.classList.add('star-hover');
                                      starContainer.classList.remove('star-dimmed');
                                    } else if (starIndex >= index && selectedValue && (starIndex + 1) <= selectedValue) {
                                      starContainer.classList.add('star-dimmed');
                                      starContainer.classList.remove('star-hover');
                                    } else {
                                      starContainer.classList.remove('star-hover', 'star-dimmed');
                                    }
                                  });
                                }}
                                onMouseLeave={() => {
                                  // Limpiar hover y mostrar solo seleccionados
                                  document.querySelectorAll(`[name="scale-${currentPage}-${idx}"]`).forEach((input, starIndex) => {
                                    const starContainer = input.closest('.scale-circle-label').querySelector('.scale-star-container');
                                    starContainer.classList.remove('star-hover', 'star-dimmed');
                                  });
                                }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px' }}
                              >
                                <svg width="48" height="48" viewBox="0 0 24 24" fill={selectedValue && val <= selectedValue ? "#FFD700" : "none"} stroke="#FFD700" strokeWidth="2">
                                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                                </svg>
                              </div>
                            )}
                            {scaleType === 'faces' && (
                              <div
                                className={`scale-face-container face-${mappedFace} ${isSelected ? 'selected' : ''}`}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px' }}
                              >
                                {getFaceIcon(index, total)}
                              </div>
                            )}
                            <input
                              type="radio"
                              name={`scale-${currentPage}-${idx}`}
                              value={val}
                              checked={answers[currentPage]?.[idx] === val}
                              onChange={() => handleAnswerChange(currentPage, {
                                ...answers[currentPage],
                                [idx]: val
                              })}
                              className="scale-radio"
                              style={{ display: 'none' }}
                            />
                          </label>
                        );
                      })}
                    </div>

                  </div>
                )}

                {/* Slider question */}
                {question.type === 'slider' && Array.isArray(question.sliders) && question.sliders.length > 0 && (
                  <div className="slider-multi-taker">
                    {question.sliders.map((slider, sIdx) => (
                      <div key={sIdx} className="slider-multi-row-taker">
                        {slider.label && (
                          <div className="slider-multi-label-taker">{slider.label}</div>
                        )}
                        <div className="slider-preview-row-taker">
                          <input
                            type="range"
                            min={slider.min}
                            max={slider.max}
                            value={answers[currentPage]?.[idx]?.[sIdx] ?? slider.min}
                            onChange={e => {
                              const newAnswers = Array.isArray(answers[currentPage]?.[idx]) ? [...answers[currentPage][idx]] : [];
                              newAnswers[sIdx] = Number(e.target.value);
                              handleAnswerChange(currentPage, {
                                ...answers[currentPage],
                                [idx]: newAnswers
                              });
                            }}
                            className="slider-preview-range-taker"
                          />
                          <span className="slider-value-taker">{answers[currentPage]?.[idx]?.[sIdx] ?? slider.min}</span>
                        </div>
                        <div className="slider-minmax-row-taker">
                          <span className="slider-label-min-taker">{slider.min}</span>
                          <span className="slider-label-max-taker">{slider.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* File upload question */}
                {question.type === 'file_upload' && (
                  <div
                    className="file-upload-preview-box"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={async e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                        const file = e.dataTransfer.files[0];
                        const filePath = await uploadFileTobackend(file);
                        handleAnswerChange(currentPage, {
                          ...answers[currentPage],
                          [idx]: { filePath, name: file.name }
                        });
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="file-upload-icon" style={{ fontSize: 20, color: '#19B394' }}>
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 36H36C40.4183 36 44 32.4183 44 28C44 23.5817 40.4183 20 36 20C35.4477 20 35 19.5523 35 19C35 14.5817 31.4183 11 24 11C16.5817 11 13 14.5817 13 19C13 19.5523 12.5523 20 12 20C7.58172 20 4 23.5817 4 28C4 32.4183 7.58172 36 12 36Z" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M24 28V18" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" />
                        <path d="M21 21L24 18L27 21" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="file-upload-title" style={{ color: '#19B394' }}>Subir un Archivo</div>
                    <div className="file-upload-subtitle">Arrastre y suelte archivos aquí</div>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={async e => {
                        const file = e.target.files[0];
                        if (file) {
                          const filePath = await uploadFileTobackend(file);
                          handleAnswerChange(currentPage, {
                            ...answers[currentPage],
                            [idx]: { filePath, name: file.name }
                          });
                        }
                      }}
                      required={question.required}
                    />
                    {answers[currentPage]?.[idx]?.name && (
                      <span style={{ marginTop: 16, color: '#333', fontSize: 15 }}>
                        Archivo seleccionado: {answers[currentPage][idx].name}
                      </span>
                    )}
                  </div>
                )}

                {question.type === 'ranking' && (
                  <div className="form-group">
                    <ul
                      style={{ listStyle: 'none', padding: 0, marginTop: 8, maxWidth: 340 }}
                    >
                      {(answers[currentPage]?.[idx] && Array.isArray(answers[currentPage]?.[idx])
                        ? answers[currentPage]?.[idx]
                        : (() => {
                          const initial = [...question.options];
                          handleAnswerChange(currentPage, {
                            ...answers[currentPage],
                            [idx]: initial
                          });
                          return initial;
                        })()
                      ).map((option, idx, arr) => (
                        <li
                          key={option + idx}
                          draggable
                          onDragStart={e => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', idx);
                            e.currentTarget.setAttribute('data-dragging', 'true');
                          }}
                          onDragEnd={e => {
                            e.currentTarget.removeAttribute('data-dragging');
                          }}
                          onDragOver={e => e.preventDefault()}
                          onDrop={e => {
                            e.preventDefault();
                            const fromIdx = Number(e.dataTransfer.getData('text/plain'));
                            if (fromIdx === idx) return;
                            const currentOrder = answers[currentPage]?.[idx] && Array.isArray(answers[currentPage]?.[idx])
                              ? answers[currentPage]?.[idx]
                              : question.options;
                            const newOrder = [...currentOrder];
                            const [moved] = newOrder.splice(fromIdx, 1);
                            newOrder.splice(idx, 0, moved);
                            handleAnswerChange(currentPage, {
                              ...answers[currentPage],
                              [idx]: newOrder
                            });
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 0, padding: '6px 0', boxShadow: 'none', cursor: 'grab',
                            minHeight: 38,
                            maxWidth: 320,
                            border: 'none',
                            borderBottom: idx !== arr.length - 1 ? '1px solid #e0e0e0' : 'none',
                            marginBottom: 0,
                            position: 'relative',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.setAttribute('data-hover', 'true');
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.removeAttribute('data-hover');
                          }}
                        >
                          {/* Icono de arrastre de 6 puntos verticales, solo visible en hover o drag, perfectamente alineado al centro de la opción */}
                          <span
                            style={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#20bfa9', fontSize: 18, minWidth: 16,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              pointerEvents: 'none',
                              height: 36,
                              marginRight: 10,
                            }}
                            className="drag-dots"
                          >
                            <span style={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 0.7, fontSize: 18 }}>
                                •<br />•<br />•
                              </span>
                              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 0.7, fontSize: 18 }}>
                                •<br />•<br />•
                              </span>
                            </span>
                          </span>
                          <span style={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            background: '#eaf7f4',
                            color: '#222',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: 18,
                            marginRight: 16,
                          }}>{idx + 1}</span>
                          <span style={{ flex: 1, fontSize: 16 }}>{option}</span>
                          <style>{`
                                  li[data-hover="true"] .drag-dots, li[data-dragging="true"] .drag-dots {
                                    opacity: 1 !important;
                                    pointer-events: auto !important;
                                    display: flex !important;
                                  }
                                `}</style>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {question.type === 'matrix' && Array.isArray(question.rows) && Array.isArray(question.columns) && (
                  <div className="matrix-taker-container">
                    <div className="matrix-preview-table-container">
                      <table className="matrix-taker-table">
                        <thead>
                          <tr>
                            <th className="matrix-corner-taker"></th>
                            {question.columns.map((col, colIdx) => (
                              <th className="matrix-col-taker" key={colIdx}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {question.rows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              <td className="matrix-row-taker">{row}</td>
                              {question.columns.map((_, colIdx) => (
                                <td className="matrix-cell-taker" key={colIdx}>
                                  <input
                                    type="radio"
                                    name={`matrix-${currentPage}-${idx}-row${rowIdx}`}
                                    checked={answers[currentPage]?.[idx]?.[rowIdx] === colIdx}
                                    onChange={() => {
                                      const newAnswers = Array.isArray(answers[currentPage]?.[idx]) ? [...answers[currentPage][idx]] : [];
                                      newAnswers[rowIdx] = colIdx;
                                      handleAnswerChange(currentPage, {
                                        ...answers[currentPage],
                                        [idx]: newAnswers
                                      });
                                    }}
                                    style={{
                                      width: '18px',
                                      height: '18px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="submit-section" style={{ marginTop: 32, display: 'flex', justifyContent: 'center', gap: 16 }}>
            {currentPage > 0 && (
              <button type="button" className="btn btn-secondary" onClick={() => setCurrentPage(currentPage - 1)}>
                Anterior página
              </button>
            )}
            {currentPage < pages.length - 1 ? (
              <button type="button" className="btn btn-primary" onClick={() => {
                // Validar todas las preguntas de la página actual
                const valid = questions.every((q, idx) => isQuestionValid(q, answers[currentPage]?.[idx]));
                if (!valid) {
                  toast.error('Por favor responde todas las preguntas obligatorias de esta página');
                  return;
                }
                setCurrentPage(currentPage + 1);
              }}>
                Siguiente página
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                Enviar encuesta
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

function isQuestionValid(question, answer) {
  if (!question.required) return true;
  if (question.type === 'checkbox') {
    return answer && answer.length > 0;
  } else if (question.type === 'scale') {
    return answer !== null && answer !== undefined;
  } else if (question.type === 'file_upload') {
    return !!answer;
  } else if (question.type === 'matrix') {
    if (!answer) return false;
    return Array.isArray(question.rows) && question.rows.every((_, rowIdx) => typeof answer[rowIdx] === 'number');
  } else if (typeof answer === 'string') {
    return answer.trim() !== '';
  } else {
    return !!answer;
  }
}

export default SurveyTaker;